import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../../firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import Layout from '../../components/ui/Layout';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import { toast } from '../../components/ui/Toast';
import {
  IconArrowLeft,
  IconSpeakerphone,
  IconCheck,
  IconAlertTriangle,
  IconBell,
  IconInfoCircle,
} from '@tabler/icons-react';

const PRIORITIES = [
  { value: 'info',      label: 'Info',      description: 'General information — appears in feed only', icon: IconInfoCircle, colour: 'var(--color-brand)' },
  { value: 'important', label: 'Important', description: 'Push notification sent to all staff',         icon: IconBell,       colour: 'var(--color-warning)' },
  { value: 'urgent',    label: 'Urgent',    description: 'Full-screen alert — must be acknowledged',    icon: IconAlertTriangle, colour: 'var(--color-danger)' },
];

const AUDIENCES = [
  { value: 'all',      label: 'Everyone'          },
  { value: 'managers', label: 'Managers only'     },
  { value: 'operators', label: 'Operators only'   },
];

export default function AnnouncementNew() {
  const navigate = useNavigate();
  const { orgId, userProfile } = useAuth();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [priority, setPriority] = useState('info');
  const [audience, setAudience] = useState('all');
  const [requiresAcknowledgement, setRequiresAcknowledgement] = useState(false);

  const handleSend = async () => {
    if (!title.trim()) { setError('Please enter a title'); return; }
    if (!message.trim()) { setError('Please enter a message'); return; }
    setSaving(true);
    setError('');
    try {
      await addDoc(collection(db, 'organisations', orgId, 'announcements'), {
        title: title.trim(),
        message: message.trim(),
        priority,
        targetAudience: audience,
        requiresAcknowledgement: priority === 'urgent' ? true : requiresAcknowledgement,
        sentBy: userProfile?.name,
        sentById: userProfile?.id,
        sentAt: serverTimestamp(),
        reads: [],
        acknowledgements: [],
        expiresAt: null,
      });
      toast.success('Announcement sent');
      navigate('/announcements');
    } catch (err) {
      console.error(err);
      setError('Failed to send announcement — please try again');
    }
    setSaving(false);
  };

  return (
    <Layout>
      <div style={{ padding: 'var(--main-padding)', maxWidth: 580, margin: '0 auto' }}>

        <div onClick={() => navigate('/announcements')} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 13, color: 'var(--color-text-secondary)', cursor: 'pointer', marginBottom: 20 }}>
          <IconArrowLeft size={15} stroke={1.5} /> Announcements
        </div>

        <h1 style={{ fontSize: 22, fontWeight: 400, marginBottom: 4 }}>Send announcement</h1>
        <p style={{ fontSize: 13, color: 'var(--color-text-tertiary)', marginBottom: 24 }}>
          Broadcast a message to your team
        </p>

        {error && (
          <div style={{ background: 'var(--color-danger-light)', border: '0.5px solid var(--color-danger)', borderRadius: 'var(--radius-md)', padding: '10px 14px', fontSize: 13, color: 'var(--color-danger)', marginBottom: 16 }}>
            {error}
          </div>
        )}

        <Card style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 14 }}>Message</div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>
              Title <span style={{ color: 'var(--color-danger)' }}>*</span>
            </label>
            <input
              type="text"
              placeholder="e.g. Farm closure — heavy rain"
              value={title}
              onChange={e => setTitle(e.target.value)}
              style={{ width: '100%', boxSizing: 'border-box' }}
              autoFocus
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>
              Message <span style={{ color: 'var(--color-danger)' }}>*</span>
            </label>
            <textarea
              placeholder="Write your announcement here..."
              value={message}
              onChange={e => setMessage(e.target.value)}
              rows={5}
              style={{ width: '100%', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit' }}
            />
          </div>
        </Card>

        <Card style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 14 }}>Priority</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {PRIORITIES.map(p => {
              const Icon = p.icon;
              return (
                <div
                  key={p.value}
                  onClick={() => setPriority(p.value)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '12px 14px', borderRadius: 'var(--radius-md)',
                    border: '0.5px solid',
                    borderColor: priority === p.value ? p.colour : 'var(--color-border-light)',
                    background: priority === p.value ? p.colour + '12' : 'transparent',
                    cursor: 'pointer', transition: 'all 0.15s',
                  }}
                >
                  <Icon size={18} color={p.colour} stroke={1.5} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, color: priority === p.value ? p.colour : 'var(--color-text-primary)', marginBottom: 2 }}>
                      {p.label}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>{p.description}</div>
                  </div>
                  {priority === p.value && <IconCheck size={15} color={p.colour} stroke={2} />}
                </div>
              );
            })}
          </div>
        </Card>

        <Card style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 14 }}>Audience</div>
          <div style={{ display: 'flex', gap: 8 }}>
            {AUDIENCES.map(a => (
              <div
                key={a.value}
                onClick={() => setAudience(a.value)}
                style={{
                  flex: 1, padding: '10px', borderRadius: 'var(--radius-md)', textAlign: 'center',
                  border: '0.5px solid',
                  borderColor: audience === a.value ? 'var(--color-brand)' : 'var(--color-border-light)',
                  background: audience === a.value ? 'var(--color-brand-light)' : 'transparent',
                  color: audience === a.value ? 'var(--color-brand)' : 'var(--color-text-secondary)',
                  fontSize: 13, cursor: 'pointer', transition: 'all 0.15s',
                }}
              >
                {a.label}
              </div>
            ))}
          </div>
        </Card>

        {priority !== 'urgent' && (
          <Card style={{ marginBottom: 20 }}>
            <div
              onClick={() => setRequiresAcknowledgement(!requiresAcknowledgement)}
              style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}
            >
              <div style={{
                width: 18, height: 18, borderRadius: 4,
                border: '1.5px solid ' + (requiresAcknowledgement ? 'var(--color-brand)' : 'var(--color-border-medium)'),
                background: requiresAcknowledgement ? 'var(--color-brand)' : '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                {requiresAcknowledgement && <IconCheck size={11} color="#fff" stroke={2.5} />}
              </div>
              <div>
                <div style={{ fontSize: 13, color: 'var(--color-text-primary)' }}>Require acknowledgement</div>
                <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginTop: 2 }}>
                  Staff must tap confirm to dismiss this announcement
                </div>
              </div>
            </div>
          </Card>
        )}

        <div style={{ display: 'flex', gap: 10 }}>
          <Button variant="secondary" onClick={() => navigate('/announcements')}>Cancel</Button>
          <Button
            variant="primary"
            fullWidth
            onClick={handleSend}
            disabled={saving}
            loading={saving}
            icon={<IconSpeakerphone size={14} stroke={1.5} />}
          >
            {saving ? 'Sending...' : 'Send announcement'}
          </Button>
        </div>

      </div>
    </Layout>
  );
}